import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Building2, ShieldCheck, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";

const FIELDS = [
  ["organization_name", "Organization Name"],
  ["organization_type", "Organization Type"],
  ["industry", "Industry"],
  ["logo_url", "Logo URL"],
  ["company_website", "Website URL"],
  ["company_description", "Company Description"],
  ["country", "Country"],
  ["state", "State"],
  ["city", "City"],
  ["address", "Address"],
  ["phone", "Phone Number"],
  ["linkedin_url", "LinkedIn URL"],
  ["registration_number", "Registration Number (optional)"],
  ["tax_number", "Tax/GST/VAT Number (optional)"],
  ["employee_count", "Number of Employees (optional)"],
  ["year_established", "Year Established (optional)"],
];

export default function OrgProfile() {
  const { user, refresh } = useAuth();
  const [completion, setCompletion] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    api.get("/profile/organization/completion").then(({ data }) => setCompletion(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) setForm({ ...user });
  }, [user]);

  const isInstitution = user?.role === "institution";
  const label = isInstitution ? "Institution" : "Organization";

  const save = async () => {
    const payload = {};
    FIELDS.forEach(([k]) => {
      if (form[k] != null && form[k] !== "") payload[k] = k === "year_established" ? parseInt(form[k]) || null : form[k];
    });
    if (form.terms_accepted) payload.terms_accepted = true;
    try {
      const { data } = await api.patch("/profile/organization", payload);
      setCompletion(data.completion);
      toast.success(`${label} profile saved`);
      setOpen(false);
      refresh && refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-ec-bg" data-testid="org-profile-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 space-y-6">
        <div>
          <div className="eyebrow">{label} Profile</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Complete your issuer profile.</h1>
          <p className="text-ec-ink-soft mt-2 max-w-2xl">
            Reach ≥80% profile completion and upload a logo to unlock certificate issuance,
            verification, and roster invitations.
          </p>
        </div>

        {completion && (
          <Card className="rounded-sm border-ec-line" data-testid="org-completion-card">
            <CardContent className="p-6">
              <div className="flex justify-between items-baseline">
                <div className="eyebrow">Profile completion</div>
                <div className="font-mono-plex text-xs text-ec-ink-soft">{completion.percent}%</div>
              </div>
              <div className="h-2 bg-ec-mute mt-3 overflow-hidden">
                <div className={`h-full transition-all duration-700 ${completion.percent >= 80 ? "bg-ec-emerald" : "bg-ec-amber"}`} style={{ width: `${completion.percent}%` }} />
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {(completion.items || []).map((it) => (
                  <div key={it.field} className="flex items-center gap-2">
                    {it.done ? <CheckCircle2 className="w-4 h-4 text-ec-emerald" /> : <AlertTriangle className="w-4 h-4 text-ec-amber" />}
                    <span className={it.done ? "line-through text-ec-ink-soft" : ""}>{it.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                <Gate ok={completion.gates?.completion_ok} label="Completion ≥ 80%" />
                <Gate ok={completion.gates?.logo_uploaded} label="Logo uploaded" />
                <Gate ok={completion.gates?.terms_accepted} label="Terms accepted" />
                <Gate ok={completion.gates?.active_subscription} label="Active subscription" />
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="open-org-profile-form"><Building2 className="w-4 h-4" /> Edit profile</Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-display">Edit {label.toLowerCase()} profile</DialogTitle>
                    </DialogHeader>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {FIELDS.map(([k, lbl]) => (
                        <Field key={k} label={lbl} long={k === "company_description" || k === "address"}>
                          {(k === "company_description" || k === "address") ? (
                            <Textarea value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`org-field-${k}`} rows={2} />
                          ) : (
                            <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`org-field-${k}`} />
                          )}
                        </Field>
                      ))}
                    </div>
                    <label className="flex items-start gap-2 text-sm mt-3 cursor-pointer">
                      <input type="checkbox" checked={!!form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })} data-testid="org-terms-checkbox" />
                      I confirm the information above is accurate and accept the Issuer Agreement.
                    </label>
                    <DialogFooter>
                      <Button onClick={save} className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2" data-testid="save-org-profile"><ShieldCheck className="w-4 h-4" /> Save profile</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Link to="/billing"><Button variant="outline" className="rounded-sm border-ec-ink gap-2"><Sparkles className="w-4 h-4" /> Manage plan</Button></Link>
              </div>
              {completion.blocking_message && (
                <div className="mt-5 border border-ec-amber/40 bg-ec-amber/5 text-ec-ink text-sm p-3 flex gap-2 items-start" data-testid="org-blocking-banner">
                  <AlertTriangle className="w-4 h-4 text-ec-amber mt-0.5" /> {completion.blocking_message}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="eyebrow">{label}</Label><div className="mt-1.5">{children}</div></div>;
}

function Gate({ ok, label }) {
  return (
    <div className={`px-3 py-2 border ${ok ? "border-ec-emerald/30 bg-ec-emerald/5" : "border-ec-amber/30 bg-ec-amber/5"} flex items-center gap-2`}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-ec-emerald" /> : <AlertTriangle className="w-4 h-4 text-ec-amber" />}
      <span className="text-sm">{label}</span>
    </div>
  );
}
